import { useLocalStorage } from '@uidotdev/usehooks';
import { type RefObject, useEffect, useRef, useState } from 'react';

import { AutoAwesome, QuestionMarkIcon, SparkleIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverDescription, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { t } from 'utils/getTranslations';
import { TOUR_STRIPE_CLASS } from './TourCard';
import { useTourMode } from './tourMode';
import { type TourSource, useTour } from './TourProvider';

type Props = {
    /** What the offer hangs under, its end against that element's end: the title row's buttons, this one included. */
    offerAnchor: RefObject<HTMLElement | null>;
};

/**
 * Where the tour of the Home widgets starts: the round question mark in Home's title row, and the offer that opens
 * from it by itself, once per person.
 *
 * Offered is kept in this browser under a key carrying the person's id, as pins are, and saved as the offer shows
 * or as the tour starts: whichever way it goes then, it does not come back. It never shows in a session that opened
 * the Welcome by itself. The Welcome saves seen as it opens, so the live flag is already true under the open
 * Welcome; the sign-in snapshot is what says it had been seen before. A Welcome opened again from the sidebar is not
 * known here. Nor does the offer show while a tour runs, which writes nothing either.
 *
 * The button is the offer's trigger, though a click on it starts the tour and never opens the offer: a popover with
 * no trigger has nowhere to hand Tab on to, and sends it from its last button to the end of the document.
 */
export default function WidgetsTourButton({ offerAnchor }: Props) {
    const { user } = useAuth();
    const { start } = useTour();
    const touring = useTourMode();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    // No initial value, which the hook would write: the key is saved as the offer shows, and no sooner.
    const [offered, setOffered] = useLocalStorage<unknown>(
        `crespect.widgets-tour-offer.${user?.id ?? 'none'}`,
        undefined
    );
    // Decided as the button mounts: saving the offer as shown must not close it.
    const [pending, setPending] = useState(
        () => !!user && offered !== true && user.preferences.newDesignWelcomeSeen === true
    );
    const [glows, setGlows] = useState(false);
    const offering = pending && !touring;
    const label = t('tour.widgets.take');

    useEffect(() => {
        if (offering) setOffered(true);
    }, [offering, setOffered]);

    // A tour taken from the button before any offer is not offered afterwards.
    const begin = (source: TourSource) => {
        setPending(false);
        setOffered(true);
        void start(source, 'widgets');
    };

    return (
        <Popover open={offering} onOpenChange={(next) => !next && setPending(false)}>
            <Tooltip content={label}>
                <PopoverTrigger
                    // A press starts the tour and never opens the offer, so the button does not say it has a popup.
                    aria-haspopup={undefined}
                    aria-expanded={undefined}
                    aria-controls={undefined}
                    render={
                        <Button
                            ref={buttonRef}
                            variant="outline"
                            size="icon"
                            aria-label={label}
                            data-tour="home-tour"
                            data-tour-glow={glows ? '' : undefined}
                            className="border-primary-wash-border bg-primary-wash text-primary hover:bg-primary-tint hover:text-primary relative flex-none rounded-full shadow-none"
                            onClick={() => begin('home')}
                        />
                    }
                >
                    <QuestionMarkIcon className="size-[18px]" />
                    <SparkleIcon
                        fill="currentColor"
                        // A hairline in the card's colour that lifts the star off the mark: the button's own 2px would eat it.
                        className="text-secondary stroke-card absolute -end-[3px] -top-[3px] size-3 stroke-[0.75px]!"
                    />
                </PopoverTrigger>
            </Tooltip>
            <PopoverContent
                anchor={offerAnchor}
                side="bottom"
                align="end"
                sideOffset={10}
                className="w-85 max-w-[calc(100vw-16px)] gap-0 overflow-hidden rounded-[12px] p-0"
                // The card takes the focus, not Not now: it opens without a focus ring drawn on a button.
                ref={cardRef}
                initialFocus={cardRef}
                finalFocus={buttonRef}
            >
                <div aria-hidden className={TOUR_STRIPE_CLASS} />
                <div className="flex flex-col gap-2.5 px-5 py-4">
                    <PopoverTitle className="text-dialog-title flex items-center gap-2.5 font-semibold">
                        <AutoAwesome className="text-primary size-5" />
                        {t('tour.widgets.offer.title')}
                    </PopoverTitle>
                    <PopoverDescription className="text-foreground-secondary leading-[1.45] text-pretty">
                        {t('tour.widgets.offer.body')}
                    </PopoverDescription>
                    <div className="flex items-center gap-2.5 pt-1">
                        <Button
                            variant="link"
                            size="sm"
                            className="text-foreground-tertiary text-meta h-auto px-0 underline"
                            onClick={() => {
                                setPending(false);
                                // Shows where the tour stays, as the New design row does for a skipped Welcome.
                                setGlows(true);
                            }}
                        >
                            {t('tour.widgets.offer.later')}
                        </Button>
                        <span className="flex-1" />
                        <Button
                            variant="secondary"
                            className="h-[34px] px-3.5 font-semibold"
                            onClick={() => begin('home-offer')}
                        >
                            {t('tour.take')}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
